'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { AlertTriangle, ArrowRight, Info, LifeBuoy, XCircle } from 'lucide-react';
import { Sheet } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { useToast } from '@/components/Toast';
import { useIsMobile } from '@/lib/viewport/client';
import { useNetworkStatus } from '@/lib/offline/use-network-status';
import { readErrorAction } from '@/lib/errors/user-action';
import * as haptics from '@/lib/native/haptics';

/**
 * A NOTICE is for the one kind of message a toast is wrong for: something the
 * owner has to act on.
 *
 * A toast is four seconds in a corner. That is right for "Copied" and wrong for
 * "this invoice can't go out until your VAT number is saved" — three sentences
 * that vanished before they were read, with directions to a page and no way of
 * getting there. Other apps don't toast those; they stop and ask, with the fix
 * one tap away. So a notice is a small <Sheet>: centred on a desktop, a bottom
 * sheet on a phone, and it stays until it is answered.
 *
 * Which to use — nothing, inline, toast or notice — is in
 * `docs/notices-and-toasts.md`. Most call sites should not use this directly:
 * `useReportFailure()` below reads a failed response and picks for them.
 */

export type NoticeTone = 'error' | 'warning' | 'info';

export type NoticeAction =
  | { label: string; href: string }
  | { label: string; onClick: () => void };

/** What "Tell the team" sends, so the ticket arrives with the facts attached. */
export interface SupportContext {
  /** The request that failed, e.g. `POST /api/email/send`. */
  request?: string;
  status?: number;
}

export interface NoticeOptions {
  title: string;
  body: ReactNode;
  tone?: NoticeTone;
  /** The first action is the primary button. "Close" is always added. */
  actions?: NoticeAction[];
  /** When set, a "Tell the team" button raises a support ticket in one tap. */
  support?: SupportContext;
}

interface QueuedNotice extends NoticeOptions {
  id: number;
}

interface NoticeContextValue {
  notice: (options: NoticeOptions) => void;
}

const NoticeContext = createContext<NoticeContextValue | null>(null);

const noopNotice: NoticeContextValue = { notice: () => {} };

export function useNotice(): NoticeContextValue {
  return useContext(NoticeContext) ?? noopNotice;
}

export function NoticeProvider({ children }: { children: ReactNode }) {
  // One at a time, first come first served. Two failures stacked on top of
  // each other read as the app falling apart; one after the other reads as
  // two things to deal with.
  const [queue, setQueue] = useState<QueuedNotice[]>([]);
  const nextId = useRef(0);

  const notice = useCallback((options: NoticeOptions) => {
    const id = nextId.current++;
    setQueue((prev) => [...prev, { ...options, id }]);
    if ((options.tone ?? 'error') === 'error') haptics.error();
  }, []);

  const dismiss = useCallback((id: number) => {
    setQueue((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const value = useMemo(() => ({ notice }), [notice]);
  const current = queue[0];

  return (
    <NoticeContext.Provider value={value}>
      {children}
      {current && <NoticeSheet key={current.id} notice={current} onClose={() => dismiss(current.id)} />}
    </NoticeContext.Provider>
  );
}

const TONE_ICON = { error: XCircle, warning: AlertTriangle, info: Info } as const;

type TicketState = 'idle' | 'sending' | 'sent' | 'already' | 'failed';

function NoticeSheet({ notice, onClose }: { notice: QueuedNotice; onClose: () => void }) {
  const isMobile = useIsMobile();
  const router = useRouter();
  const online = useNetworkStatus();
  const tone = notice.tone ?? 'error';
  const primaryRef = useRef<HTMLButtonElement>(null);
  const [ticket, setTicket] = useState<TicketState>('idle');

  /**
   * Focus the way forward. Same race as SaveCheck in Sheet.tsx: <Sheet> pulls
   * focus to its own container in a setTimeout(0) on mount, so autoFocus alone
   * loses half the time. Child effects run first, so this timer fires after.
   */
  useEffect(() => {
    const t = window.setTimeout(() => primaryRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, []);

  const runAction = (action: NoticeAction) => {
    onClose();
    if ('href' in action) router.push(action.href);
    else action.onClick();
  };

  const tellTheTeam = async () => {
    setTicket('sending');
    try {
      const res = await fetch('/api/support/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: notice.title,
          message: typeof notice.body === 'string' ? notice.body : notice.title,
          page: window.location.pathname,
          request: notice.support?.request,
          status: notice.support?.status,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setTicket('failed'); return; }
      setTicket(data?.outcome === 'already' ? 'already' : 'sent');
    } catch {
      setTicket('failed');
    }
  };

  const [primary, ...rest] = notice.actions ?? [];

  return (
    <Sheet
      portal
      isMobile={isMobile}
      onClose={onClose}
      className={`mucka-notice mucka-notice--${tone}`}
      backdropClassName="mucka-notice-backdrop"
    >
      <Sheet.Header
        title={
          <span className="mucka-notice-title">
            <span className="mucka-notice-icon" aria-hidden>
              <Icon icon={TONE_ICON[tone]} size={18} />
            </span>
            {notice.title}
          </span>
        }
      />
      <Sheet.Body withFooter>
        <div className="mucka-notice-body t-body-md">{notice.body}</div>
        {notice.support && ticket !== 'idle' && ticket !== 'sending' && (
          <p className="mucka-notice-ticket t-body-sm" role="status">
            {ticket === 'sent' && "Sent to the team, with the details of what went wrong. We'll be in touch — you'll see the reply under Settings → Support."}
            {ticket === 'already' && "You've already told the team about this one — it's with them. You'll see the reply under Settings → Support."}
            {ticket === 'failed' && "That didn't go through. Try again in a moment, or tell Mucka in the chat."}
          </p>
        )}
      </Sheet.Body>
      <Sheet.Footer className="mucka-notice-actions">
        {notice.support && (
          <Button
            variant="ghost"
            size="md"
            leadingIcon={LifeBuoy}
            trailingIcon={null}
            className="mucka-notice-support"
            disabled={!online || ticket === 'sending' || ticket === 'sent' || ticket === 'already'}
            title={online ? undefined : "You're offline — this needs a connection."}
            onClick={tellTheTeam}
          >
            {ticket === 'sending' ? 'Sending…' : ticket === 'sent' || ticket === 'already' ? 'Team told' : 'Tell the team'}
          </Button>
        )}
        {rest.map((a) => (
          <Button key={a.label} variant="secondary" size="md" trailingIcon={null} onClick={() => runAction(a)}>
            {a.label}
          </Button>
        ))}
        <Button
          ref={primary ? undefined : primaryRef}
          variant={primary ? 'secondary' : 'primary'}
          size="md"
          trailingIcon={null}
          onClick={onClose}
        >
          Close
        </Button>
        {primary && (
          <Button
            ref={primaryRef}
            variant="primary"
            size="md"
            trailingIcon={'href' in primary ? ArrowRight : null}
            onClick={() => runAction(primary)}
          >
            {primary.label}
          </Button>
        )}
      </Sheet.Footer>
    </Sheet>
  );
}

// ── Reporting a failed request ─────────────────────────────────────────────

export interface FailureReport {
  /** The HTTP status, or 0 when the request never reached us. */
  status: number;
  /** The parsed JSON body, if there was one. */
  body?: unknown;
  /** What to say when the server said nothing useful. */
  fallback: string;
  /** e.g. `POST /api/email/send` — goes on the ticket if they tell the team. */
  request?: string;
}

/**
 * The request never reached us: offline, or the connection dropped. A toast,
 * because there is nothing to do but try again and the offline banner already
 * says the rest. One sentence for it everywhere, instead of "Network error."
 */
export const NETWORK_FAILURE: FailureReport = {
  status: 0,
  fallback: "Couldn't reach Mucka. Check your connection and try again.",
};

/**
 * The one way a screen reports a failed request. It decides the surface so the
 * call site does not have to:
 *
 * - the server attached an `action` → a NOTICE with that button;
 * - the server fell over (5xx) → a NOTICE with "Tell the team";
 * - anything else (a plain 4xx) → a toast with the server's own words.
 *
 * Always the server's sentence when there is one (anti-pattern #35): it is the
 * only participant that knows what went wrong.
 */
export function useReportFailure() {
  const { notice } = useNotice();
  const { failureToast } = useToast();

  return useCallback((report: FailureReport) => {
    const body = (report.body && typeof report.body === 'object' ? report.body : {}) as Record<string, unknown>;
    const message = typeof body.error === 'string' && body.error.trim() ? body.error : report.fallback;
    const title = typeof body.title === 'string' && body.title.trim() ? body.title : null;
    const action = readErrorAction(body.action);
    const support: SupportContext = { request: report.request, status: report.status || undefined };

    if (action?.kind === 'link') {
      notice({ tone: 'warning', title: title ?? 'Needs a quick fix', body: message, actions: [{ label: action.label, href: action.href }] });
      return;
    }
    if (action?.kind === 'support' || report.status >= 500) {
      notice({ tone: 'error', title: title ?? 'Something went wrong at our end', body: message, support });
      return;
    }
    // A plain, self-explanatory refusal: a toast is the right size for it.
    failureToast(message);
  }, [notice, failureToast]);
}
